import { useRef } from 'react';
import { DocumentIcon } from '@heroicons/react/24/outline';
import RowActionMenu from '../menu/RowActionMenu';
import { MIME_LABEL, CHIP_COLORS } from './constants';
import { getExtension, formatFileSize, formatDate, getDisplayFileName } from './helpers';
import styles from '../../styles/assetsManagement/filesManager.module.scss';

const EXPLORER_COLS = 6;

const ExplorerView = ({ files, rowActionButtons }) => {
  const cardRefs = useRef([]);

  const handleCardKeyDown = (e, idx) => {
    const total = files.length;
    let next = idx;
    if (e.key === 'ArrowRight')     { e.preventDefault(); next = Math.min(idx + 1, total - 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); next = Math.max(idx - 1, 0); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); next = Math.min(idx + EXPLORER_COLS, total - 1); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); next = Math.max(idx - EXPLORER_COLS, 0); }
    if (next !== idx) cardRefs.current[next]?.focus();
  };

  return (
    <div className={styles.explorerGrid} role="list" aria-label="Files as icons">
      {files.map((fileItem, idx) => {
        const displayName = getDisplayFileName(fileItem);
        const label = MIME_LABEL[fileItem.fileType] || getExtension(displayName) || '?';
        const { bg, color } = CHIP_COLORS[label] || { bg: '#f1f5f9', color: '#475569' };
        const scanLabel =
          fileItem.scannedStatus === 'SCANNING' ? 'Scanning' :
          fileItem.scannedStatus === 'CLEAN' ? 'Clean' :
          fileItem.scannedStatus === 'INFECTED' ? 'Infected' :
          fileItem.scannedStatus === 'ERROR' ? 'Error' :
          null;

        return (
          <div
            key={fileItem.id}
            ref={(el) => { cardRefs.current[idx] = el; }}
            className={styles.explorerCard}
            tabIndex={0}
            role="listitem"
            aria-label={`${displayName}, ${formatFileSize(fileItem.fileSize)}`}
            onKeyDown={(e) => handleCardKeyDown(e, idx)}
            title={`${displayName}\n${formatFileSize(fileItem.fileSize)}\nUploaded ${formatDate(fileItem.uploadedAt)}`}
          >
            <div className={styles.explorerIconArea}>
              <div className={styles.explorerDocIcon}>
                <DocumentIcon />
                <div className={styles.explorerDocCorner} />
                <span className={styles.explorerTypeBadge} style={{ backgroundColor: bg, color }}>
                  {label}
                </span>
              </div>
              {fileItem.duplicate && (
                <span className={styles.explorerDupDot} title="Duplicate content" />
              )}
            </div>

            <div className={styles.explorerCardInfo}>
              <p className={styles.explorerCardName} title={displayName}>
                {displayName}
              </p>
              <p className={styles.explorerCardMeta}>
                {formatFileSize(fileItem.fileSize)}
                {fileItem.folderName && ` • ${fileItem.folderName}`}
                {scanLabel && ` • ${scanLabel}`}
              </p>
            </div>

            <div className={styles.explorerCardActions}>
              <RowActionMenu menuItems={rowActionButtons} rowData={fileItem} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExplorerView;
